import dayjs from "dayjs";

/** Extracts a file_name from a value that arrives JSON-serialized as a string. */
const extractSerializedFileName = (
  type: string | undefined,
  value: unknown,
): string | null => {
  // check if is a file and is coming serialized in a string
  if ((type === "file" || type === "url") && typeof value === "string") {
    try {
      const parsedFile = JSON.parse(value);
      if (
        typeof parsedFile === "object" &&
        parsedFile !== null &&
        "file_name" in parsedFile
      ) {
        return String((parsedFile as { file_name: string }).file_name);
      }
    } catch (e) {
      console.warn("Error parsing file value:", e);
    }
  }
  return null;
};

/** Formats a primitive (string/number) value, applying date formatting when needed. */
const formatPrimitiveValue = (
  type: string | undefined,
  value: string | number,
  fallback: string,
): string => {
  if (type === "date" || type === "datetime") {
    if (typeof value === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      return value;
    }
    const format = type === "date" ? "DD/MM/YYYY" : "DD/MM/YYYY HH:mm";
    return dayjs(value).isValid() ? dayjs(value).format(format) : fallback;
  }
  return String(value);
};

/** Formats a { start, end } date-range object. */
const formatDateRangeValue = (
  value: { start: unknown; end: unknown },
  fallback: string,
): string => {
  try {
    if (
      value.start &&
      value.end &&
      typeof value === "object" &&
      "start" in value &&
      "end" in value &&
      typeof value.start === "string" &&
      typeof value.end === "string" &&
      dayjs(value.start).isValid() &&
      dayjs(value.end).isValid()
    ) {
      return `${dayjs(value.start).format("DD/MM/YYYY")} - ${dayjs(
        value.end,
      ).format("DD/MM/YYYY")}`;
    } else {
      return fallback;
    }
  } catch {
    return fallback;
  }
};

/** Joins the non-empty values of a generic object into a comma-separated string. */
const formatGenericObjectValue = (value: object): string => {
  const values = Object.values(value).filter(
    (val) => val !== null && val !== undefined && val !== "",
  );
  if (values.length > 0) {
    return values.join(", ");
  }
  return "";
};

const formatValue = (
  type: string | undefined,
  value: unknown,
  fallback: string,
): string => {
  const serializedFileName = extractSerializedFileName(type, value);
  if (serializedFileName !== null) {
    return serializedFileName;
  }

  if (typeof value === "string" || typeof value === "number") {
    return formatPrimitiveValue(type, value, fallback);
  }

  if (
    type === "date_range" &&
    typeof value === "object" &&
    value !== null &&
    "start" in value &&
    "end" in value
  ) {
    return formatDateRangeValue(value, fallback);
  }

  // list item object with key and label
  if (
    typeof value === "object" &&
    value !== null &&
    "key" in value &&
    "label" in value
  ) {
    return String(value.label);
  }

  if (
    type === "identification" &&
    typeof value === "object" &&
    value !== null &&
    "value" in value
  ) {
    return value.value as string;
  }

  // search some file name
  if (typeof value === "object" && value !== null && "file_name" in value) {
    return String((value as { file_name: string }).file_name);
  }

  // object with multiple keys/values
  if (typeof value === "object" && value !== null) {
    return formatGenericObjectValue(value);
  }
  return "";
};

export const getFormattedFieldValue = (
  type: string | undefined,
  value: unknown,
  defaultString = "No asignado",
): string[] => {
  if (Array.isArray(value)) {
    // arreglo de valores
    const values = value
      .map((val) => formatValue(type, val, defaultString))
      .filter((v): v is string => v !== null);
    if (values.length > 0) {
      return [...values];
    }
  } else {
    const formatted = formatValue(type, value, defaultString);
    if (formatted) {
      return [formatted];
    }
  }
  return [defaultString];
};
